import React, { useRef, useState } from 'react';
import { Upload, X, FileText, Loader2, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

function resolveUrl(url) {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  if (url.startsWith('/api/')) {
    return `${process.env.REACT_APP_BACKEND_URL || ''}${url}`;
  }
  return url;
}

export function ImageUploader({ value, onChange, endpoint = '/upload/foto-pejabat', label = 'Upload Foto', size = 120, testid }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const preview = resolveUrl(value);

  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange?.(data.url, data);
      toast.success('Foto berhasil diunggah');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal upload');
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = '';
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div
        className="relative rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden"
        style={{ width: size, height: size }}
      >
        {preview ? (
          <img src={preview} alt="preview" className="w-full h-full object-cover" />
        ) : (
          <ImageIcon className="w-8 h-8 text-slate-300" />
        )}
        {busy && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-red-600" />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <input ref={ref} type="file" accept="image/*" onChange={handle} className="hidden" data-testid={testid || 'image-upload-input'} />
        <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={busy}>
          <Upload className="w-4 h-4 mr-2" />{label}
        </Button>
        {value && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange?.('')} className="text-red-600">
            <X className="w-3 h-3 mr-1" /> Hapus
          </Button>
        )}
        <p className="text-[10px] text-slate-500">PNG/JPG/WEBP · maks 20MB</p>
      </div>
    </div>
  );
}

export function DocumentUploader({ items = [], onChange, endpoint = '/upload/dokumen-agenda', kindOptions, testid }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [currentKind, setCurrentKind] = useState(kindOptions?.[0]?.value || 'lampiran');

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBusy(true);
    const uploaded = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('kind', currentKind);
      try {
        const { data } = await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        uploaded.push({ name: data.name, url: data.url, kind: data.kind });
      } catch (err) {
        toast.error(`Gagal upload ${f.name}: ${err.response?.data?.detail || err.message}`);
      }
    }
    if (uploaded.length) {
      onChange?.([...items, ...uploaded]);
      toast.success(`${uploaded.length} dokumen berhasil diunggah`);
    }
    setBusy(false);
    if (ref.current) ref.current.value = '';
  };

  const remove = (idx) => {
    onChange?.(items.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {kindOptions && kindOptions.length > 0 && (
          <select
            className="text-xs h-9 px-2 rounded border border-slate-200 bg-white"
            value={currentKind}
            onChange={(e) => setCurrentKind(e.target.value)}
          >
            {kindOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}
        <input ref={ref} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" onChange={handle} className="hidden" data-testid={testid || 'doc-upload-input'} />
        <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={busy}>
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          Tambah Dokumen
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-500 italic">Belum ada dokumen.</div>
      ) : (
        <div className="space-y-1.5">
          {items.map((d, i) => (
            <div key={i} className="flex items-center gap-2 p-2 rounded border border-slate-200 bg-white">
              <FileText className="w-4 h-4 text-slate-400" />
              <a href={resolveUrl(d.url)} target="_blank" rel="noreferrer" className="text-sm font-medium text-slate-700 hover:text-red-600 flex-1 truncate">{d.name}</a>
              {d.kind && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{d.kind}</span>}
              <button type="button" onClick={() => remove(i)} className="p-1 hover:bg-red-50 text-red-600 rounded"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function fileUrl(url) {
  return resolveUrl(url);
}
